import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics } from '@/mocks/comics';

const pageImages = [
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20page%20artwork%20with%20detailed%20characters%20and%20dialogue%20bubbles%2C%20clean%20lineart%20style%2C%20black%20and%20white%20with%20soft%20grayscale%20shading%2C%20dramatic%20panel%20layout%2C%20expressive%20character%20poses%2C%20Korean%20comic%20style%2C%20high%20quality%20illustration&width=1200&height=900&seq=reader-p01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20interior%20page%20with%20character%20closeup%20and%20speech%20bubbles%2C%20clean%20lineart%2C%20grayscale%20tones%2C%20dynamic%20panel%20composition%2C%20emotional%20facial%20expressions%2C%20manhwa%20style%20illustration&width=1200&height=900&seq=reader-p02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20action%20comic%20page%20with%20fighting%20scene%20and%20speed%20lines%2C%20grayscale%20art%2C%20dramatic%20panel%20layout%2C%20detailed%20background%2C%20manhwa%20style%20artwork&width=1200&height=900&seq=reader-p03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20comic%20manhwa%20page%20with%20romantic%20scene%20and%20gentle%20expressions%2C%20black%20and%20white%20illustration%2C%20soft%20screen%20tone%20shading%2C%20beautiful%20character%20design%2C%20Korean%20comic%20style&width=1200&height=900&seq=reader-p04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20page%20with%20dialogue%20and%20reaction%20shots%2C%20clean%20lineart%2C%20grayscale%2C%20varied%20panel%20sizes%2C%20expressive%20character%20art%2C%20professional%20comic%20illustration&width=1200&height=900&seq=reader-p05&orientation=landscape',
];

export default function ComicReaderPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  const [currentChapter, setCurrentChapter] = useState(1);
  const [loadedPages, setLoadedPages] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const totalPages = 20;

  useEffect(() => {
    setLoadedPages([1, 2, 3]);
  }, [currentChapter]);

  const loadMore = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);
    setTimeout(() => {
      setLoadedPages((prev) => {
        const lastPage = prev[prev.length - 1] || 0;
        const newPages: number[] = [];
        for (let i = lastPage + 1; i <= Math.min(lastPage + 3, totalPages); i++) {
          newPages.push(i);
        }
        return [...prev, ...newPages];
      });
      setIsLoading(false);
    }, 600);
  }, [isLoading]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && loadedPages.length < totalPages) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => observer.disconnect();
  }, [loadMore, loadedPages.length]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-search-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画不存在或已下架</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white rounded-full px-6 py-2.5 text-sm transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const currentChapterData = comic.chapters.find(
    (ch) => ch.number === currentChapter
  );

  const getPageImage = (pageNum: number) => {
    const idx = (pageNum - 1) % pageImages.length;
    return pageImages[idx];
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ComicStory',
          name: `${comic.title} - 第${currentChapter}话`,
          description: comic.description,
          url: `http://rfeff.com/comic/${comic.slug}/read`,
          author: {
            '@type': 'Person',
            name: comic.author,
          },
          image: comic.coverImage,
          isPartOf: {
            '@type': 'ComicSeries',
            name: comic.title,
            url: `http://rfeff.com/comic/${comic.slug}`,
          },
          potentialAction: {
            '@type': 'ReadAction',
            target: `http://rfeff.com/comic/${comic.slug}/read`,
            expectsAcceptanceOf: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'CNY',
              availability: 'https://schema.org/InStock',
            },
          },
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://rfeff.com/' },
            { '@type': 'ListItem', position: 2, name: '全部分类', item: 'http://rfeff.com/category/all' },
            { '@type': 'ListItem', position: 3, name: comic.title, item: `http://rfeff.com/comic/${comic.slug}` },
            { '@type': 'ListItem', position: 4, name: '阅读' },
          ],
        })}
      </script>

      <Navbar />

      <main className="pt-20 bg-background-50 min-h-screen">
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '全部分类', to: '/category/all' },
              { label: comic.title, to: `/comic/${comic.slug}` },
              { label: '阅读' },
            ]}
          />

          <section className="py-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <Link
                  to={`/comic/${comic.slug}`}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                </Link>
                <div>
                  <h1 className="text-base md:text-lg font-heading font-bold text-foreground-950">
                    {comic.title}
                  </h1>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    第{currentChapter}话 · {currentChapterData?.title}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentChapter(Math.max(1, currentChapter - 1))}
                  disabled={currentChapter === 1}
                  className="whitespace-nowrap flex items-center gap-1 px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </button>
                <select
                  value={currentChapter}
                  onChange={(e) => setCurrentChapter(Number(e.target.value))}
                  className="px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 bg-background-50 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {comic.chapters.map((ch) => (
                    <option key={ch.id} value={ch.number}>
                      第{ch.number}话 {ch.title}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setCurrentChapter(Math.min(comic.chapters.length, currentChapter + 1))}
                  disabled={currentChapter === comic.chapters.length}
                  className="whitespace-nowrap flex items-center gap-1 px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  下一话 <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-foreground-400 mb-4">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-calendar-line"></i>
              </div>
              <span>更新日期：{currentChapterData?.updatedAt}</span>
              <span className="mx-2">·</span>
              <span>智能加载 · 向下滚动阅读</span>
            </div>

            <div className="space-y-1">
              {loadedPages.map((pageNum) => (
                <div
                  key={`${currentChapter}-${pageNum}`}
                  className="w-full bg-background-100 rounded-lg overflow-hidden"
                >
                  <img
                    src={getPageImage(pageNum)}
                    alt={`${comic.title} - 第${currentChapter}话 第${pageNum}页`}
                    title={`${comic.title} 第${currentChapter}话 第${pageNum}页`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
              ))}

              <div ref={loadMoreRef} className="py-8 text-center">
                {isLoading && (
                  <div className="flex items-center justify-center gap-2 text-foreground-500">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                    </div>
                    <span className="text-sm">智能加载中...</span>
                  </div>
                )}
                {!isLoading && loadedPages.length >= totalPages && (
                  <div className="text-sm text-foreground-400 py-4">
                    本章已全部加载完毕
                  </div>
                )}
                {!isLoading && loadedPages.length < totalPages && (
                  <button
                    onClick={loadMore}
                    className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer py-4"
                  >
                    加载更多内容
                  </button>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}